import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Target, Microscope, LineChart } from "lucide-react";
import { CtaBanner } from "../components/sections/CtaBanner";
import { COMPANY, STATS } from "../lib/constants";

const CITY_IMG =
  "https://images.unsplash.com/photo-1664841383940-c46ee63d5542?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHx2aWN0b3JpYSUyMGJjJTIwbmlnaHQlMjBza3lsaW5lfGVufDB8fHx8MTc3NzU3NDcwN3ww&ixlib=rb-4.1.0&q=85";

const PRINCIPLES = [
  {
    icon: Microscope,
    title: "Honest with the numbers",
    color: "#38BDF8",
    body: "We share the numbers you actually need — not the ones that make us look good. If something isn't working, you'll hear it from us first.",
  },
  {
    icon: Target,
    title: "We test, we learn, we tune",
    color: "#F472B6",
    body: "We try lots of ad ideas, test different audiences, and learn fast from what works. No account ever runs on autopilot.",
  },
  {
    icon: LineChart,
    title: "Built to grow with you",
    color: "#A78BFA",
    body: "Our approach is built for businesses going from $10k a month in ads to $500k+ a month — without things falling apart along the way.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="page-about">
      {/* HEADER */}
      <section className="relative pt-20 pb-20 md:pt-28 md:pb-24 overflow-hidden border-b border-zinc-800/70">
        <div className="rm-glow-top-right" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="rm-overline mb-5">About RevMatrix</div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter text-white max-w-4xl leading-[0.95]">
            We're a small agency with a{" "}
            <span className="text-violet-400">big opinion</span> about how
            ads should bring in real customers.
          </h1>
        </div>
      </section>

      {/* STORY + CITY */}
      <section
        data-testid="about-story"
        className="relative py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="rm-overline mb-4 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-violet-400" />
              Based in Victoria, B.C.
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-tight">
              Founded on the Pacific coast. Built for businesses anywhere.
            </h2>
            <div className="mt-6 space-y-5 text-zinc-400 leading-relaxed">
              <p>
                RevMatrix started because we kept seeing the same thing:
                founders burning budget on agencies that never actually touched
                their accounts, and in-house teams pushed toward feel-good
                numbers that had nothing to do with sales.
              </p>
              <p>
                We built the agency we wanted to hire — small on purpose, senior
                on every account, obsessed with running ads that actually bring
                in leads. From Victoria, B.C., we work with online stores,
                software founders, and service businesses all across North
                America.
              </p>
              <p className="text-zinc-300">
                Pricing is tailored, onboarding is fast, and every project
                starts with a free first call.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="about-story-cta"
              className="rm-btn-primary mt-8"
            >
              Talk to us <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-6">
            <div className="relative rm-card overflow-hidden p-0">
              <div
                className="aspect-[4/5] bg-cover bg-center"
                style={{ backgroundImage: `url(${CITY_IMG})` }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/30 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="rm-overline text-violet-300 mb-2">
                  HQ — 48.43°N / 123.37°W
                </div>
                <div className="font-display text-2xl md:text-3xl font-bold text-white">
                  {COMPANY.location}
                </div>
                <div className="font-mono text-xs text-zinc-400 mt-2">
                  {COMPANY.phoneDisplay} · {COMPANY.email}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section
        data-testid="principles-section"
        className="relative py-20 md:py-28 border-t border-zinc-800/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-2xl">
            <div className="rm-overline mb-4">Operating principles</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white">
              Three things we refuse to compromise on.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRINCIPLES.map((p) => (
              <div
                key={p.title}
                data-testid={`principle-${p.title.toLowerCase()}`}
                className="rm-card rm-service-card p-8"
                style={{
                  "--brand": p.color,
                  "--brand-glow": `${p.color}55`,
                }}
              >
                <div
                  className="h-12 w-12 flex items-center justify-center border border-zinc-800 bg-zinc-900/60 mb-6 rm-service-icon-tile transition-all duration-300"
                  style={{ color: p.color }}
                >
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white mb-3">
                  {p.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section
        data-testid="about-stats"
        className="relative py-20 border-t border-zinc-800/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`about-stat-${i}`}
              className="border-l-2 border-violet-500/60 pl-5"
            >
              <div className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white">
                {s.value}
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
